import React from "react";
import { motion } from "framer-motion";
import { ExternalLink, Github, GitBranch, GitCommit, ArrowUpRight, FileCode2, CircleDot } from "lucide-react";
import SectionHeading from "./SectionHeading";
import { projects } from "../data/mock";

const accentText = {
  cyan: "text-cyan-300",
  amber: "text-amber-300",
  emerald: "text-emerald-300"
};
const accentBg = {
  cyan: "bg-cyan-400",
  amber: "bg-amber-400",
  emerald: "bg-emerald-400"
};
const statusStyles = {
  deployed: "bg-emerald-500/10 text-emerald-300 border-emerald-400/30",
  production: "bg-cyan-500/10 text-cyan-300 border-cyan-400/30",
  live: "bg-emerald-500/10 text-emerald-300 border-emerald-400/30",
  planning: "bg-white/5 text-zinc-400 border-white/10"
};

const ProjectCard = ({ p, index }) => {
  const isPlaceholder = p.placeholder;
  return (
    <motion.article
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, delay: (index % 3) * 0.08 }}
      whileHover={{ y: -4 }}
      className="group relative rounded-2xl glass-strong overflow-hidden hover:border-white/15 transition-all duration-400"
    >
      {/* PR header chrome */}
      <div className="flex items-center gap-2.5 h-11 px-4 border-b border-white/5 bg-[#0a0b10]">
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-red-500/80" />
          <span className="w-2 h-2 rounded-full bg-amber-400/80" />
          <span className="w-2 h-2 rounded-full bg-emerald-400/80" />
        </div>
        <span className="font-mono text-[11px] text-zinc-400 truncate">
          vedbora/{p.title.toLowerCase().replace(/\s+/g, "-")}
        </span>
        <span className={`ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[9px] font-mono uppercase tracking-wider ${statusStyles[p.status]}`}>
          <CircleDot className="w-2 h-2" />
          {p.status}
        </span>
      </div>

      {/* Body */}
      <div className="p-5 md:p-6">
        {/* Title row */}
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="font-display text-xl md:text-2xl font-semibold text-white leading-tight">
              {p.title}
            </h3>
            <p className="text-sm text-zinc-400 mt-1">{p.subtitle}</p>
          </div>
        </div>

        {/* Branch + Commits meta */}
        <div className="flex items-center flex-wrap gap-3 mb-4 pb-4 border-b border-white/5">
          <span className="inline-flex items-center gap-1.5 font-mono text-[10px] text-zinc-500">
            <GitBranch className="w-3 h-3" />
            {p.branch}
          </span>
          <span className="inline-flex items-center gap-1.5 font-mono text-[10px] text-zinc-500">
            <GitCommit className="w-3 h-3" />
            {p.commits} commits
          </span>
          {p.period && (
            <span className="inline-flex items-center gap-1.5 font-mono text-[10px] text-zinc-500">
              <span className={`w-1.5 h-1.5 rounded-full ${accentBg[p.accent]}`} />
              {p.period}
            </span>
          )}
        </div>

        {/* Case study fields */}
        <div className={`space-y-3 ${isPlaceholder ? "opacity-60" : ""}`}>
          <Field label="problem" accent={p.accent}>{p.problem}</Field>
          <Field label="solution" accent={p.accent}>{p.solution}</Field>
          <Field label="impact" accent={p.accent}>{p.impact}</Field>
        </div>

        {/* File tree */}
        {p.files && p.files.length > 0 && (
          <div className="mt-5 p-3 rounded-lg bg-black/30 border border-white/5">
            <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-zinc-600 mb-2">
              key files
            </p>
            <div className="flex flex-wrap gap-1.5">
              {p.files.map((f) => (
                <span
                  key={f}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded bg-white/[0.03] border border-white/5 font-mono text-[10px] text-zinc-300"
                >
                  <FileCode2 className="w-2.5 h-2.5 text-cyan-400" />
                  {f}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Tech chips */}
        <div className="mt-5 flex flex-wrap gap-1.5">
          {p.techStack.map((t) => (
            <span
              key={t}
              className="px-2.5 py-1 rounded-md bg-white/5 border border-white/5 font-mono text-[10px] text-zinc-300"
            >
              {t}
            </span>
          ))}
        </div>

        {/* Actions */}
        <div className="mt-6 flex flex-wrap items-center gap-2">
          {p.liveUrl ? (
            <a
              href={p.liveUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 px-4 h-10 rounded-lg bg-cyan-400 hover:bg-cyan-300 text-zinc-950 text-sm font-semibold transition-colors"
            >
              Live Demo
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-4 h-10 rounded-lg bg-white/5 text-zinc-500 text-sm font-medium border border-white/5 cursor-not-allowed">
              Demo pending
            </span>
          )}
          <a
            href={p.githubUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 px-4 h-10 rounded-lg glass hover:border-cyan-400/30 text-zinc-200 text-sm font-medium transition-colors"
          >
            <Github className="w-3.5 h-3.5" />
            Source
          </a>
          <span className="ml-auto inline-flex items-center gap-1 text-zinc-600 group-hover:text-cyan-300 transition-colors">
            <ArrowUpRight className="w-4 h-4" />
          </span>
        </div>
      </div>
    </motion.article>
  );
};

const Field = ({ label, children, accent }) => (
  <div className="flex gap-3">
    <span className={`font-mono text-[9px] tracking-[0.25em] uppercase pt-1 w-16 shrink-0 ${accentText[accent]}`}>
      {label}
    </span>
    <p className="flex-1 text-[13px] text-zinc-300 leading-relaxed">{children}</p>
  </div>
);

const Projects = () => {
  const real = projects.filter((p) => !p.placeholder);
  const placeholders = projects.filter((p) => p.placeholder);

  return (
    <section id="projects" className="relative py-24 md:py-32">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-400/20 to-transparent" />
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <SectionHeading
          eyebrow="Projects"
          title="Case studies in backend engineering."
          description="Real systems with real trade-offs. Each project card reads like a pull request: problem, solution, impact."
        />

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {real.map((p, i) => (
            <ProjectCard key={p.id} p={p} index={i} />
          ))}
          {placeholders.map((p, i) => (
            <ProjectCard key={p.id} p={p} index={real.length + i} />
          ))}
        </div>

        <div className="mt-10 text-center">
          <p className="font-mono text-xs text-zinc-500">
            &gt; more repos on{" "}
            <a
              href="https://github.com/vedbora"
              target="_blank"
              rel="noreferrer"
              className="text-cyan-300 hover:text-cyan-200 underline decoration-dotted"
            >
              github.com/vedbora
            </a>
          </p>
        </div>
      </div>
    </section>
  );
};

export default Projects;
